/// <amd-module name="TFS/Admin/Groups/Pivots/Settings/Redux/SettingsPivotSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { DataProviders } from "TFS/Admin/Common/Constants/DataProviderConstants";
import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { Avatar, AvatarSize } from "TFS/Admin/Common/Contracts/VSS.Profile";
import { addMembersToGroup, IAddMembersToGroupResult, listMembership } from "TFS/Admin/Common/DataAccessLayers/GraphDAL";
import { GraphClientName, IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { SettingsOperation } from "TFS/Admin/Groups/Common/Constants";
import { TeamAdmin } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import { getTeamAdmins as getAdmins, updateTeamProperties } from "TFS/Admin/Groups/Common/Redux/DataSource/AdminTeamsDataSource";
import { getErrorMessage, IErrorAction } from "TFS/Admin/Groups/Common/Util/ErrorMessageHelper";
import { GroupsComponent, GroupsTelemetryLogger } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { SettingsPivotActions, SettingsPivotActionTypes } from "TFS/Admin/Groups/Pivots/Settings/Redux/SettingsPivotActions";
import * as Resources from "TFS/Admin/Groups/Pivots/Settings/Resources";
import { localeFormat } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { GraphGroup, GraphMembership, GraphTraversalDirection } from "VSS/Platform/Contracts/Graph";
import { IdentityRef, JsonPatchDocument, Operation } from "VSS/Platform/Contracts/WebApi";
import { IIdentity } from "VSSUI/IdentityPicker";
import { MessageCardSeverity } from "VSSUI/MessageCard";

/**
 * Root groups settings saga.
 */
export function* settingsPivotSaga(): SagaIterator {
    yield effects.takeEvery(SettingsPivotActionTypes.InitView, initView);
    yield effects.takeEvery(SettingsPivotActionTypes.SaveChangesClick, saveChanges);
    yield effects.takeEvery(SettingsPivotActionTypes.SaveImageConfirm, saveImage);
    yield effects.takeEvery(SettingsPivotActionTypes.DeleteGroupConfirm, deleteGroup);
    yield effects.takeEvery(SettingsPivotActionTypes.RemoveAdmin, removeAdmin);
    yield effects.takeEvery(SettingsPivotActionTypes.SaveAdmins, saveAdmins);
}

export function* initView(action: ActionsOfType<SettingsPivotActions, SettingsPivotActionTypes.InitView>): SagaIterator {
    const { selectedGroup, isTeamsPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.SettingsPivot,
        "Initialize settings pivot",
        isTeamsPage ? Feature.Teams : undefined
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

    if (!selectedGroup || !selectedGroup) {
        yield effects.call(telemetryLogger.captureError, "No selected group provided");
        return;
    }
    try {
        if (!selectedGroup.actionPermissions) {
            yield effects.put(GroupsPivotActions.getGroupPermissions(selectedGroup));
        }

        const avatar: Avatar = (yield effects.call([graphClient, graphClient.getAvatar], selectedGroup.descriptor, AvatarSize.Large)) as any;
        yield effects.put(SettingsPivotActions.initViewSuccess(avatar));
        if (isTeamsPage) {
            yield effects.call(getTeamAdmins, selectedGroup);
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* saveChanges(action: ActionsOfType<SettingsPivotActions, SettingsPivotActionTypes.SaveChangesClick>): SagaIterator {
    const { groupName, groupDescription, groupDescriptor } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.SettingsPivot, "Save groups change");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
    var jsonPatchDocument: JsonPatchDocument = [
        {
            op: Operation.Replace,
            path: "/displayName",
            value: groupName.trim()
        },
        {
            op: Operation.Replace,
            path: "/description",
            value: groupDescription
        }
    ];
    try {
        const graphGroup: GraphGroup = (yield effects.call([graphClient, graphClient.updateGroup], groupDescriptor, jsonPatchDocument)) as any;
        yield effects.put(SettingsPivotActions.saveChangesSuccess());
        yield effects.put(GroupsViewActions.groupEdited(graphGroup));

    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(
            SettingsPivotActions.saveChangesFailure(
                getErrorMessage(GroupsComponent.SettingsPivot, error, IErrorAction.Edit),
                MessageCardSeverity.Error)
        );
    }
}

export function* saveImage(action: ActionsOfType<SettingsPivotActions, SettingsPivotActionTypes.SaveImageConfirm>): SagaIterator {
    const { groupDescriptor, groupAvatar, resetImage, imageData } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.SettingsPivot, "Update image");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
    try {
        if (resetImage) {
            yield effects.call([graphClient, graphClient.deleteAvatar], groupDescriptor);
        } else if (groupAvatar && groupAvatar.value.length > 0) {
            yield effects.call([graphClient, graphClient.setAvatar], groupAvatar, groupDescriptor);
        }
        if (imageData) {
            yield effects.put(GroupsViewActions.groupAvatarUpdatedUrl(imageData.url));
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    } finally {
        yield effects.put(SettingsPivotActions.saveImageFinished());
    }
}

export function* deleteGroup(action: ActionsOfType<SettingsPivotActions, SettingsPivotActionTypes.DeleteGroupConfirm>): SagaIterator {
    const group = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    var telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.SettingsPivot, "Delete group");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
    try {
        yield effects.call([graphClient, graphClient.deleteGroup], group.descriptor);
        yield effects.put(SettingsPivotActions.settingsOperationStatus(SettingsOperation.DeleteGroup, "", MessageCardSeverity.Info, group));
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(
            SettingsPivotActions.settingsOperationStatus(
                SettingsOperation.DeleteGroup,
                getErrorMessage(GroupsComponent.SettingsPivot, error, IErrorAction.Delete),
                MessageCardSeverity.Error,
                group
            )
        );
    }
}

function* getTeamAdmins(team: GraphGroup): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupsPivot,
        "getTeamAdmins",
        Feature.Teams
    );

    try {
        const { admins, members } = (yield effects.all({
            admins: effects.call(getAdmins, pageContext, team.descriptor),
            members: effects.call(listMembership, team.descriptor, telemetryLogger, GraphTraversalDirection.Down)
        })) as any;
        if (!admins) {
            throw new Error("An error occurred when fetching team's admins");
        }
        yield effects.put(
            SettingsPivotActions.getTeamAdminsSuccess(
                (admins as IdentityRef[]).map(
                    identity =>
                    ({
                        identity,
                        isTeamAdmin: true,
                        isMember: members.findIndex((x: GraphMembership) => identity.descriptor == x.memberDescriptor) > -1
                    } as TeamAdmin)
                )
            )
        );
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* removeAdmin(action: ActionsOfType<SettingsPivotActions, SettingsPivotActionTypes.RemoveAdmin>): SagaIterator {
    const { admin, selectedGroup } = action.payload;

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsPivot, "Add Team", Feature.Teams);

    const successCode = 204;

    try {
        const response: any = (yield effects.call(updateTeamProperties, pageContext, {
            removeAdmins: true,
            teamDescriptor: selectedGroup.descriptor,
            adminDescriptor: admin.identity.descriptor
        })) as any;
        if (!response || response.statusCode !== successCode) {
            throw new Error("Data provider did not return success (204) response");
        }

        yield effects.put(SettingsPivotActions.removeAdminSuccess(admin));
        yield effects.put(
            SettingsPivotActions.settingsOperationStatus(
                SettingsOperation.RemoveAdmin,
                localeFormat(Resources.removeAdminSuccess, admin.identity.displayName),
                MessageCardSeverity.Info,
                selectedGroup
            )
        );
    } catch (error) {
        yield effects.call(
            telemetryLogger.captureDataProviderCall,
            DataProviders.RemoveTeamAdmin,
            false,
            "Data provider did not return success (204) response"
        );
        yield effects.put(
            SettingsPivotActions.settingsOperationStatus(
                SettingsOperation.RemoveAdmin,
                Resources.removeAdminError,
                MessageCardSeverity.Error,
                selectedGroup
            )
        );
    }
}

export function* saveAdmins(action: ActionsOfType<SettingsPivotActions, SettingsPivotActionTypes.SaveAdmins>): SagaIterator {
    const { admins, selectedGroup, adminAsMember } = action.payload;

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsPivot, "Add Team", Feature.Teams);

    const successCode = 204;
    try {
        // Create the admins as a member
        if (adminAsMember) {
            const supportServicePrincipals = false;
            const { success, failed } = (yield effects.call(addMembersToGroup, admins, selectedGroup, supportServicePrincipals, telemetryLogger)) as unknown as IAddMembersToGroupResult;
            if (failed.length > 0) {
                yield effects.put(
                    SettingsPivotActions.settingsOperationStatus(
                        SettingsOperation.AddAdmins,
                        Resources.addAdminError,
                        MessageCardSeverity.Error,
                        selectedGroup
                    )
                );
            }

            // map success to subject descriptors
            for (let admin of admins) {
                if (!admin.subjectDescriptor) {
                    for (const entry of success) {
                        if (entry.subjectIdentity === admin) {
                            admin.subjectDescriptor = entry.memberDescriptor;
                            break;
                        }
                    }
                }
            }
        }

        // Add team admins
        const response: any = (yield effects.call(updateTeamProperties, pageContext, {
            setTeamAdmins: true,
            teamDescriptor: selectedGroup.descriptor,
            admins: admins.map(e => e.subjectDescriptor)
        })) as any;
        if (!response || response.statusCode !== successCode) {
            throw new Error("Data provider did not return success (204) response");
        }

        yield effects.put(SettingsPivotActions.onSaveAdminsSuccess(admins, adminAsMember));
        yield effects.put(
            SettingsPivotActions.settingsOperationStatus(
                SettingsOperation.AddAdmins,
                localeFormat(Resources.addAdminsSuccess, admins.length),
                MessageCardSeverity.Info,
                selectedGroup
            )
        );
    } catch (error) {
        yield effects.call(
            telemetryLogger.captureDataProviderCall,
            DataProviders.SetTeamAdmins,
            false,
            "Data provider did not return success (204) response"
        );
        yield effects.put(
            SettingsPivotActions.settingsOperationStatus(
                SettingsOperation.AddAdmins,
                Resources.addAdminError,
                MessageCardSeverity.Error,
                selectedGroup
            )
        );
    }
}
